import React, { useState, useEffect, useRef } from 'react';
import mqtt from 'mqtt';

const DEVICES = [
  { id: 'sound', label: 'Ears 1', topic: 'home/sensors/sound' },
  { id: 'sound2', label: 'Ears 2', topic: 'home/sensors/sound2' },
];

function SoundDashboard() {
  const [activeDevice, setActiveDevice] = useState(() => localStorage.getItem('soundDevice') || 'sound');
  const [sensorData, setSensorData] = useState({
    rms: [],
    state: 'connecting',
    ts: 0,
    zcr: 0,
    rms_avg: 0,
    rms_max: 0,
    peak: 0
  });
  const [status, setStatus] = useState('Connecting...');
  const [waveformHistory, setWaveformHistory] = useState([]);
  
  // For the gauge animation
  const [displayValue, setDisplayValue] = useState(0);
  const peakHold = useRef(0);
  const peakHoldTime = useRef(0);
  
  const NOISE_FLOOR = 150;
  const PEAK_HOLD_DURATION = 1200;
  const DECAY_RATE = 0.92;
  const MAX_SOUND = 6000;

  const switchDevice = (deviceId) => {
    setActiveDevice(deviceId);
    localStorage.setItem('soundDevice', deviceId);
    // Reset state on switch
    setSensorData({ rms: [], state: 'connecting', ts: 0, zcr: 0, rms_avg: 0, rms_max: 0, peak: 0 });
    setWaveformHistory([]);
    peakHold.current = 0;
    peakHoldTime.current = 0;
    setDisplayValue(0);
  };

  useEffect(() => {
    const device = DEVICES.find(d => d.id === activeDevice) || DEVICES[0];
    const brokerUrl = 'wss://kidr78home.duckdns.org/mqtt';
    const client = mqtt.connect(brokerUrl);

    client.on('connect', () => {
      setStatus('Connected');
      client.subscribe(device.topic);
    });

    client.on('error', (err) => {
      setStatus('Connection Error');
      console.error('MQTT Error:', err);
    });

    client.on('message', (_, message) => {
      try {
        const data = JSON.parse(message.toString());
        setSensorData(data);
        
        const effectiveValue = data.rms_avg > NOISE_FLOOR ? data.rms_avg : 0;
        if (effectiveValue > peakHold.current) {
          peakHold.current = effectiveValue;
          peakHoldTime.current = Date.now();
        }
        
        if (Array.isArray(data.rms)) {
          setWaveformHistory(prev => {
            const newHistory = [...prev, ...data.rms];
            return newHistory.slice(-250);
          });
        }
      } catch (err) {
        console.error('Error parsing MQTT message:', err);
      }
    });

    const decayInterval = setInterval(() => {
      const now = Date.now();
      const timeSincePeak = now - peakHoldTime.current;
      
      if (timeSincePeak > PEAK_HOLD_DURATION && peakHold.current > 0) {
        peakHold.current = Math.max(0, peakHold.current * DECAY_RATE - 3);
      }
      setDisplayValue(peakHold.current);
    }, 50);

    return () => {
      client.end();
      clearInterval(decayInterval);
    };
  }, [activeDevice]);

  // Gauge rotation
  const rotation = (displayValue / MAX_SOUND) * 180 - 90;
  
  const getColor = (val) => {
    const ratio = val / MAX_SOUND;
    if (ratio < 0.15) return '#2ecc71';
    if (ratio < 0.4) return '#f1c40f';
    return '#e74c3c';
  };
  
  const accentColor = getColor(displayValue);
  
  const getStateColor = (state) => {
    switch(state) {
      case 'armed': return '#3498db';
      case 'streaming': return '#e74c3c';
      case 'idle': return '#95a5a6';
      default: return '#f1c40f';
    }
  };

  const getStateIcon = (state) => {
    switch(state) {
      case 'armed': return '🎯';
      case 'streaming': return '📡';
      case 'idle': return '💤';
      default: return '⏳';
    }
  };

  // Generate waveform path for SVG
  const generateWaveformPath = () => {
    if (waveformHistory.length < 2) return '';
    
    const width = 600;
    const height = 120;
    const centerY = height / 2;
    const samples = waveformHistory;
    const step = width / samples.length;
    
    let path = `M 0 ${centerY}`;
    
    samples.forEach((val, i) => {
      const x = i * step;
      // Normalize and apply noise floor
      const normalized = val > NOISE_FLOOR ? (val - NOISE_FLOOR) / (MAX_SOUND - NOISE_FLOOR) : 0;
      const y = centerY - (normalized * centerY * 0.9);
      path += ` L ${x} ${y}`;
    });
    
    return path;
  };

  // Generate bar visualization for current RMS array
  const currentRmsArray = Array.isArray(sensorData.rms) ? sensorData.rms : [];

  return (
    <div className="sound-page">
      <style>{`
        .sound-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          padding: 80px 20px 40px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        .sound-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .page-title {
          color: white;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .connection-status {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 500;
        }
        
        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .card-title {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 1rem;
          text-align: center;
        }
        
        .gauge-card {
          text-align: center;
        }
        
        .needle {
          transition: transform 0.1s linear;
          transform-origin: 100px 100px;
        }
        
        .rms-value {
          font-size: 3rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.25rem;
        }
        
        .rms-label {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        
        .state-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 1rem;
          border-radius: 15px;
          margin-bottom: 1rem;
        }
        
        .state-icon { font-size: 2rem; }
        
        .state-text {
          font-size: 1.5rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }
        
        .stat-item {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 0.75rem;
          text-align: center;
        }
        
        .stat-value {
          font-size: 1.3rem;
          font-weight: 700;
          color: white;
        }
        
        .stat-label {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .waveform-card {
          grid-column: 1 / -1;
        }
        
        .waveform-container {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          padding: 1rem;
          overflow: hidden;
        }
        
        .waveform-svg {
          width: 100%;
          height: 120px;
        }
        
        .waveform-path {
          fill: none;
          stroke: url(#waveGradient);
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;
        }
        
        .bars-card {
          grid-column: 1 / -1;
        }
        
        .bars-container {
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: 2px;
          height: 100px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          padding: 1rem;
        }
        
        .bar {
          flex: 1;
          max-width: 12px;
          border-radius: 2px 2px 0 0;
          transition: height 0.05s ease;
          min-height: 2px;
        }
        
        .device-select {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.25);
          border-radius: 20px;
          color: white;
          padding: 0.4rem 1rem;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          padding-right: 2rem;
          min-height: 36px;
          transition: all 0.2s ease;
        }
        
        .device-select:hover {
          background-color: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.4);
        }
        
        .device-select:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.3);
        }
        
        .device-select option {
          background: #1a1a2e;
          color: white;
        }
        
        .header-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        
        @media (max-width: 768px) {
          .sound-page { padding: 70px 15px 30px; }
          .page-title { font-size: 2rem; }
          .dashboard-grid { grid-template-columns: 1fr; }
          .rms-value { font-size: 2.5rem; }
          .bars-container { height: 80px; }
          .bar { max-width: 8px; }
        }
      `}</style>

      <div className="sound-header">
        <div className="header-row">
          <h1 className="page-title">🔊 Sound Monitor</h1>
          <select
            className="device-select"
            value={activeDevice}
            onChange={(e) => switchDevice(e.target.value)}
            aria-label="Select sound device"
          >
            {DEVICES.map(d => (
              <option key={d.id} value={d.id}>📡 {d.label}</option>
            ))}
          </select>
        </div>
        <div 
          className="connection-status"
          style={{ 
            background: status === 'Connected' ? 'rgba(46, 204, 113, 0.2)' : 'rgba(241, 196, 15, 0.2)',
            color: status === 'Connected' ? '#2ecc71' : '#f1c40f'
          }}
        >
          <span className="status-dot" style={{ background: status === 'Connected' ? '#2ecc71' : '#f1c40f' }} />
          {status}
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Gauge Card */}
        <div className="card gauge-card">
          <div className="card-title">Peak Level</div>
          <svg width="240" height="140" viewBox="0 0 200 120" style={{ margin: '0 auto', display: 'block' }}>
            <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="15" strokeLinecap="round" />
            <path d="M 20 100 A 80 80 0 0 1 73 32" fill="none" stroke="#2ecc71" strokeWidth="3" opacity="0.5" />
            <path d="M 73 32 A 80 80 0 0 1 127 32" fill="none" stroke="#f1c40f" strokeWidth="3" opacity="0.5" />
            <path d="M 127 32 A 80 80 0 0 1 180 100" fill="none" stroke="#e74c3c" strokeWidth="3" opacity="0.5" />
            <g className="needle" style={{ transform: `rotate(${rotation}deg)` }}>
              <line x1="100" y1="100" x2="100" y2="25" stroke={accentColor} strokeWidth="4" strokeLinecap="round" />
              <circle cx="100" cy="100" r="12" fill="#1a1a2e" stroke={accentColor} strokeWidth="3" />
            </g>
          </svg>
          <div className="rms-value" style={{ color: accentColor, textShadow: `0 0 20px ${accentColor}66` }}>
            {sensorData.rms_avg || 0}
          </div>
          <div className="rms-label">Avg Level</div>
        </div>

        {/* Stats Card */}
        <div className="card">
          <div className="card-title">Statistics</div>
          <div className="state-indicator" style={{ background: `${getStateColor(sensorData.state)}22` }}>
            <span className="state-icon">{getStateIcon(sensorData.state)}</span>
            <span className="state-text" style={{ color: getStateColor(sensorData.state) }}>{sensorData.state}</span>
          </div>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#3498db' }}>{sensorData.rms_avg || 0}</div>
              <div className="stat-label">Avg RMS</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#e74c3c' }}>{sensorData.rms_max || 0}</div>
              <div className="stat-label">Max RMS</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#9b59b6' }}>{sensorData.zcr || 0}</div>
              <div className="stat-label">Zero Cross</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#2ecc71' }}>{currentRmsArray.length}</div>
              <div className="stat-label">Samples</div>
            </div>
          </div>
        </div>

        {/* Real-time Bars */}
        <div className="card bars-card">
          <div className="card-title">Current Frame ({currentRmsArray.length} samples)</div>
          <div className="bars-container">
            {currentRmsArray.map((val, i) => (
              <div
                key={i}
                className="bar"
                style={{
                  height: `${Math.max(2, Math.min(100, ((val - NOISE_FLOOR) / (MAX_SOUND - NOISE_FLOOR)) * 100))}%`,
                  background: getColor(val)
                }}
              />
            ))}
          </div>
        </div>

        {/* Waveform History */}
        <div className="card waveform-card">
          <div className="card-title">Waveform History (Last 5 seconds)</div>
          <div className="waveform-container">
            <svg className="waveform-svg" viewBox="0 0 600 120" preserveAspectRatio="none">
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#667eea" />
                  <stop offset="50%" stopColor="#764ba2" />
                  <stop offset="100%" stopColor="#e74c3c" />
                </linearGradient>
              </defs>
              {/* Center line */}
              <line x1="0" y1="60" x2="600" y2="60" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              {/* Waveform */}
              <path className="waveform-path" d={generateWaveformPath()} />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SoundDashboard;
